import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import InputText from 'components/Inputs/inputText';
import OutputErrors from 'components/Inputs/outputErrors';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ROUTE_PATH } from 'routes';
import { ArrowLeft } from 'lucide-react';
import InputEmail from 'components/Inputs/inputEmail';
import InputPassword from 'components/Inputs/inputPassword';
import { STATUS_CODE } from 'constants/apiConf';
import { useSelector } from 'react-redux';
import FormLayout from 'layouts/FormLayout';
import Button from 'components/Buttons/Button';
import { Tabs } from 'xland-ui';

function LoginForm({
  literals, literalsCommon, platform, onLogin, cleanErrors, setShowRecoveryForm,
}) {
  const [email, setEmail] = useState(window?.LOCAL_USER?.email || '');
  const [password, setPassword] = useState(window?.LOCAL_USER?.password || '');
  const [mode, setMode] = useState('investor');
  const [inputToken, setInputToken] = useState('');
  const [token2AF, setToken2AF] = useState();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const loginError = useSelector(state => state.login.loginError);

  const [searchParams] = useSearchParams();
  const invite = searchParams.get('invite');
  const inviteFundraise = searchParams.get('inviteFundraise');
  const referral = searchParams.get('referral');

  useEffect(() => {
    if (platform.oauth.google && window.google) {
      /* global google */
      google.accounts.id.initialize({
        client_id: platform.oauth.google,
        callback: ({ credential }) => {
          const params = {
            oauth: { type: 'google', id: credential },
            invite: invite || null,
            mode,
          };

          setLoading(true);
          onLogin(params).catch(() => {
            setPassword('');
            setLoading(false);
          });
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleOnClick = () => {
    google.accounts.id.prompt();
  };

  const logIn = (e) => {
    e.preventDefault();

    if (email !== '' && password !== '') {
      const body = {
        email,
        password,
        mode,
        invite: invite || null,
        second_factor: token2AF ? {
          type: 'otp',
          value: inputToken,
        } : null,
      };
      setLoading(true);
      onLogin(body)
        .then(({ status, data }) => {
          if (!token2AF && status === STATUS_CODE.ACCEPTED) {
            setToken2AF(data);
          }
          setLoading(false);
        }).catch(() => {
          setPassword('');
          setLoading(false);
        });
    }
  };

  const onEnterKeyPress = (e) => {
    if (e.charCode === 13 || e.keyCode === 13) {
      logIn(e);
    }
  };

  if (token2AF) {
    return (
      <form className='form' onSubmit={logIn} onKeyDown={onEnterKeyPress}>
        <InputText
          preText={literals.token}
          placeholder={literals.token}
          value={inputToken}
          onChange={setInputToken}
          isRequired
        />
        <OutputErrors errors={loginError ? [literals.invalidToken] : null} />
        <div className='form-actions'>
          <Button
            text={literalsCommon.send}
            type='submit'
            disabled={!inputToken}
            loading={loading}
          />
        </div>
        <div className='d-flex align-items-center' onClick={() => { setToken2AF(null); setInputToken(''); cleanErrors(); }}>
          <ArrowLeft size={14} className='me-2' />
          {literals.recovery.back}
        </div>
      </form>
    );
  }

  return (
    <FormLayout title={literals.title} className='form-auth'>
      <form onSubmit={logIn} onKeyDown={onEnterKeyPress}>
        <InputEmail
          preText={literals.email}
          placeholder={literals.email}
          isRequired
          value={email}
          onChange={value => setEmail(value)}
        />
        <InputPassword
          preText={literals.password}
          placeholder={literals.password}
          isRequired
          value={password}
          onChange={value => setPassword(value)}
        />
        <Tabs
          className='w-100'
          activeTab={mode}
          onClick={setMode}
          tabs={[
            { key: 'founder', name: literals.founder },
            { key: 'investor', name: literals.investor },
          ]}
        />
        <div className='goto-recovery-password'>
          <span onClick={() => setShowRecoveryForm(true)}>{literals.recoveryButton}</span>
        </div>
        <OutputErrors errors={loginError ? [literals.invalidLogin] : null} />
        <div className='form-actions'>
          <Button
            text={literals.login}
            type='submit'
            disabled={!email || !password}
            loading={loading}
            color='primary'
          />
          {
            platform.oauth.google && window.google ? (
              <div className='social-actions'>
                You can also sign in with&nbsp;
                <span onClick={handleOnClick}>
                  GOOGLE
                </span>
              </div>
            ) : null
          }
          {(platform.properties.signup.public || inviteFundraise || referral) && (
            <div className='social-actions'>
              {literals.questionNotRegistered}
              &nbsp;
              <Link
                className=''
                to={{
                  pathname: ROUTE_PATH.SIGNUP,
                  state: location.state,
                  search: location?.search,
                }}
              >
                <span>{literals.createAccount}</span>
              </Link>
            </div>
          )}
        </div>
      </form>
    </FormLayout>
  );
}

LoginForm.propTypes = {
  platform: PropTypes.object.isRequired,
  literals: PropTypes.object.isRequired,
  literalsCommon: PropTypes.object.isRequired,
  onLogin: PropTypes.func.isRequired,
  cleanErrors: PropTypes.func.isRequired,
  setShowRecoveryForm: PropTypes.func.isRequired,
};

export default LoginForm;
